import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  forwardRef,
  ReactNode,
  RefObject
} from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from '../Loader';
import { FallbackView } from '../FallbackView';

type RefProp<T> = RefObject<T | null> | null;

interface EndlessScrollProps {
  items: Readonly<{ length: number }>;
  loading: boolean;
  onLoadMore: () => void;
  isStopLoad: boolean;
  forwardedRef: RefProp<HTMLDivElement>;
  scrollContainer?: 'window' | 'parent';
  loadingPlaceholder?: ReactNode;
}

const EndlessScroll: React.FC<
  EndlessScrollProps & {
    Component: React.ComponentType<{ forwardedRef: RefProp<HTMLDivElement> }>;
  }
> = ({
  onLoadMore,
  loading,
  isStopLoad,
  forwardedRef,
  scrollContainer = 'window',
  loadingPlaceholder,
  items,
  Component,
  ...props
}) => {
  const { t } = useTranslation();
  const ticking = useRef(false);
  const pendingRetryRef = useRef(false);
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    setIsOffline(!navigator.onLine);
  }, []);

  const attemptLoadMore = useCallback(() => {
    if (loading || isStopLoad) return;

    if (scrollContainer === 'parent') {
      const el = forwardedRef?.current;
      if (!el) return;

      const threshold = 150;
      const isNearBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;

      if (isNearBottom) {
        onLoadMore();
      }

      return;
    }

    if (!forwardedRef?.current) return;

    const { top, height } = forwardedRef.current.getBoundingClientRect();
    const bottom = top + height;
    const offset = window.innerHeight;

    const hasScrolledPast = bottom < 0;
    const isWithinLoadDistance = bottom - offset <= window.innerHeight;

    if (!hasScrolledPast && isWithinLoadDistance) {
      onLoadMore();
    }
  }, [onLoadMore, loading, isStopLoad, forwardedRef, scrollContainer]);

  const handleScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;

    requestAnimationFrame(() => {
      ticking.current = false;
      if (!navigator.onLine) return;
      attemptLoadMore();
    });
  }, [attemptLoadMore]);

  useEffect(() => {
    const target =
      scrollContainer === 'parent' ? forwardedRef?.current : window;

    if (!target) return;

    target.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      target.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, scrollContainer, forwardedRef]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      pendingRetryRef.current = true;
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!pendingRetryRef.current || loading || !navigator.onLine) return;

    pendingRetryRef.current = false;
    attemptLoadMore();
  }, [isOffline, loading, attemptLoadMore]);

  const isLoadingMore = loading && !!items.length && !isStopLoad;
  const showOfflineMessage = isOffline && !!items.length && !isStopLoad;

  const renderState = () => {
    if (showOfflineMessage) {
      return (
        <FallbackView
          width={150}
          height={150}
          title={t('common.no_connection_title')}
          text={t('common.no_connection_text')}
        />
      );
    }

    if (loadingPlaceholder) return null;

    return <Loader loading={isLoadingMore} endless />;
  };

  const { children, ...restProps } = props as { children?: ReactNode };
  const asChildArray = (node: ReactNode): ReactNode[] => {
    if (Array.isArray(node)) return node;
    return node === undefined || node === null ? [] : [node];
  };
  const componentChildren =
    isLoadingMore && loadingPlaceholder
      ? [...asChildArray(children), ...asChildArray(loadingPlaceholder)]
      : children;
  const componentProps = { ...restProps, children: componentChildren };

  return (
    <>
      <Component
        {...(componentProps as typeof props)}
        forwardedRef={forwardedRef}
      />
      {renderState()}
    </>
  );
};

export const withEndlessScroll = <P extends object>(
  Component: React.ComponentType<
    P & { forwardedRef: RefObject<HTMLDivElement> }
  >
) => {
  const ForwardedComponent = forwardRef<
    HTMLDivElement,
    P & Omit<EndlessScrollProps, 'forwardedRef'>
  >((props, ref) => (
    <EndlessScroll
      {...props}
      forwardedRef={typeof ref !== 'function' ? ref : null}
      Component={
        Component as React.ComponentType<{
          forwardedRef: RefProp<HTMLDivElement>;
        }>
      }
    />
  ));

  const name = Component.displayName || Component.name;
  ForwardedComponent.displayName = `withEndlessScroll(${name})`;

  return ForwardedComponent;
};
